export function getAssistantArtifactId(payload: unknown): string | null {
  if (!payload || typeof payload !== "object") return null;
  const record = payload as Record<string, unknown>;
  for (const key of ["id", "messageId", "message_id", "assistantMessageId", "assistant_message_id"]) {
    if (typeof record[key] === "string" && record[key]) return record[key] as string;
  }
  return null;
}

export function getWizardOsNavigation(payload: unknown): string {
  if (!payload || typeof payload !== "object") throw new Error("App queue response is invalid.");
  const record = payload as Record<string, unknown>;
  if (typeof record.navigate_to === "string" && /^\/(?:applications|operating-systems)\/[^/]+$/.test(record.navigate_to)) {
    return record.navigate_to.replace(/^\/operating-systems\//, "/applications/");
  }
  if (typeof record.operating_system_id === "string" && record.operating_system_id) {
    return `/applications/${encodeURIComponent(record.operating_system_id)}`;
  }
  throw new Error("App queue response is missing its Applyt route.");
}

export function getWizardResumeNavigation(payload: unknown): string {
  if (!payload || typeof payload !== "object") throw new Error("Wizard start response is invalid.");
  const resumeToken = (payload as Record<string, unknown>).resume_token;
  if (typeof resumeToken === "string" && resumeToken.trim()) {
    return `/wizard?resume=${encodeURIComponent(resumeToken)}`;
  }
  throw new Error("Wizard start response is missing its resume token.");
}

export const WIZARD_EXISTING_ACCOUNT_LOGIN_MESSAGE =
  "That email already has an Applyt account. Log in to resume this route.";

export const WIZARD_UNSUPPORTED_OUTCOME_TITLE = "We do not have expertise for this outcome yet";
export const WIZARD_UNSUPPORTED_OUTCOME_DESCRIPTION =
  "Applyt builds Apps only in the supported expert areas. Try another outcome or browse the current expertise.";
export const WIZARD_UNSUPPORTED_OUTCOME_PRIMARY_CTA = "Try another outcome";
export const WIZARD_UNSUPPORTED_OUTCOME_SECONDARY_CTA = "Browse expertise";

export function isWizardExistingAccountError(value: unknown): boolean {
  const messages: string[] = [];
  if (typeof value === "string") messages.push(value);
  if (value instanceof Error) messages.push(value.message);
  if (value && typeof value === "object") {
    const record = value as Record<string, unknown>;
    if (typeof record.error === "string") messages.push(record.error);
    if (typeof record.message === "string") messages.push(record.message);
  }

  return messages.some((message) => {
    const normalized = message.trim().toLowerCase();
    return normalized.includes("account already exists") && (
      normalized.includes("sign in to resume") ||
      normalized.includes("log in to resume")
    );
  });
}

export const WIZARD_V3_MAX_QUESTIONS = 10;

export function wizardAllowsCustomAnswer(wizardVersion: number | null | undefined, allowCustomAnswer?: boolean): boolean {
  return allowCustomAnswer !== false;
}

export function getWizardQuestionProgress({
  questionIndex,
  totalQuestions,
}: {
  wizardVersion: number | null | undefined;
  questionIndex: number;
  totalQuestions: number;
  sectionQuestionIndex: number;
  sectionQuestionsLength: number;
}) {
  return {
    primaryLabel: `Question ${Math.min(questionIndex + 1, WIZARD_V3_MAX_QUESTIONS)} of up to ${WIZARD_V3_MAX_QUESTIONS}`,
    secondaryLabel: `${Math.max(totalQuestions, 1)} generated`,
    value: Math.min(((questionIndex + 1) / WIZARD_V3_MAX_QUESTIONS) * 100, 100),
  };
}

export function getWizardReviewContextSummary(result: unknown): string | null {
  if (!result || typeof result !== "object") return null;
  const value = (result as { context_summary?: unknown }).context_summary;
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

export function getWizardReviewEditAffectedLabel({
  questionIndex,
  answerCount,
}: {
  questionIndex: number;
  answerCount: number;
}): string | null {
  const affectedStart = questionIndex + 2;
  if (!Number.isInteger(questionIndex) || !Number.isInteger(answerCount) || affectedStart > answerCount) {
    return null;
  }
  return affectedStart === answerCount
    ? `question ${affectedStart}`
    : `questions ${affectedStart}-${answerCount}`;
}
